import { useState, useEffect } from 'react'
import { Modal } from '@/components/Modal'
import apiService from '@/api/service'
import type { Comment, CommentDetail } from '@/types/api'

interface ReplyModalProps {
    isOpen: boolean
    onClose: () => void
    comment: Comment | CommentDetail | null
}

interface GeneratedReply {
    id: number
    text: string
}

export const ReplyModal = ({ isOpen, onClose, comment }: ReplyModalProps): JSX.Element => {
    const [generatedReplies, setGeneratedReplies] = useState<GeneratedReply[]>([])
    const [selectedReplyId, setSelectedReplyId] = useState<number | null>(null)
    const [isGenerating, setIsGenerating] = useState(false)

    // 當 comment 改變時，重置 Modal 狀態
    useEffect(() => {
        if (comment) {
            console.log('ReplyModal: 評論改變，重置狀態')
            setGeneratedReplies([])
            setSelectedReplyId(null)
            setIsGenerating(false)
        }
    }, [comment])

    if (!comment) return <></>

    // 將評分轉換為數字，確保正確顯示星級
    const rating = parseFloat(comment.rating) || 0

    // 格式化日期（如果是 ISO 格式）
    const formatDate = (dateString: string): string => {
        // 如果已經是相對時間（如 "1 週前"），直接返回
        if (!dateString.includes('T')) {
            return dateString
        }

        try {
            const date = new Date(dateString)
            const year = date.getFullYear()
            const month = date.getMonth() + 1
            const day = date.getDate()
            const hours = date.getHours()
            const minutes = date.getMinutes()
            const seconds = date.getSeconds()

            const period = hours >= 12 ? '下午' : '上午'
            const displayHours = hours % 12 || 12

            return `${year}年${month}月${day}日  ${period}${displayHours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        } catch (error) {
            return dateString
        }
    }

    // 生成 AI 回覆
    const handleGenerateReply = async () => {
        console.log('=== 開始生成 AI 回覆 ===')
        console.log('評論資料:', comment)
        setIsGenerating(true)

        try {
            const storeCode = apiService.getCurrentStoreCode()
            console.log('店家代碼:', storeCode)

            if (!storeCode) {
                console.error('未找到店家代碼')
                alert('未找到店家代碼，請重新登入')
                setIsGenerating(false)
                return
            }

            const requestParams = {
                store_code: storeCode,
                caption: comment.caption,
                id_review: comment.id_review || `comment_${Date.now()}`
            }

            console.log('=== API 請求參數 ===')
            console.log('store_code:', requestParams.store_code)
            console.log('caption:', requestParams.caption)
            console.log('id_review:', requestParams.id_review)
            console.log('完整請求參數:', requestParams)

            console.log('正在調用 replyComments API...')
            const response = await apiService.replyComments(requestParams)

            console.log('=== API 響應結果 ===')
            console.log('響應狀態:', response.status)
            console.log('響應訊息:', response.message)
            console.log('響應時間戳:', response.timestamp)
            console.log('回覆數量:', response.data?.length || 0)
            console.log('完整響應:', response)

            // 檢查 API 響應狀態
            if (response.status === 'error' && response.message === 'This tokens is invalid, please login again') {
                console.warn('Token 無效，跳轉到登入頁面')
                // 清除本地存儲的 token
                localStorage.removeItem('token')
                localStorage.removeItem('store_code')
                // 關閉 modal 並跳轉到登入頁面
                onClose()
                window.location.href = '/login'
                return
            }

            console.log('=== 處理 API 響應 ===')
            console.log('原始回覆數據:', response.data)

            const apiReplies: GeneratedReply[] = response.data.map((text, index) => ({
                id: index + 1,
                text: text
            }))

            console.log('轉換後的回覆格式:', apiReplies)
            console.log('回覆數量:', apiReplies.length)

            setGeneratedReplies(apiReplies)
            setSelectedReplyId(1)

            console.log('=== AI 回覆生成完成 ===')
        } catch (error) {
            console.error('=== API 調用失敗 ===')
            console.error('錯誤類型:', error instanceof Error ? error.constructor.name : typeof error)
            console.error('錯誤訊息:', error instanceof Error ? error.message : String(error))
            console.error('完整錯誤:', error)
            alert('生成回覆失敗，請重試')
        } finally {
            setIsGenerating(false)
            console.log('=== 生成流程結束 ===')
        }
    }

    // 複製回覆到剪貼板
    const handleCopyReply = async () => {
        const selectedReply = generatedReplies.find(r => r.id === selectedReplyId)
        if (!selectedReply) {
            alert('請先選擇一個回覆')
            return
        }

        try {
            await navigator.clipboard.writeText(selectedReply.text)
            alert('回覆已複製到剪貼板！')
        } catch (error) {
            console.error('複製失敗:', error)
            alert('複製失敗，請重試')
        }
    }

    // 切換選中的回覆
    const handleSelectReply = (id: number) => {
        setSelectedReplyId(id)
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="bg-white overflow-hidden w-full md:w-[90vw] lg:w-[800px] md:max-w-[90vw] lg:max-w-[800px] h-auto md:min-h-[700px] lg:min-h-[867px] flex flex-col rounded-[15px] md:rounded-[20px] shadow-2xl max-h-[90vh]">
                {/* 標題列 - 响应式 */}
                <div className="w-full h-[60px] md:h-[85px] lg:h-[95.41px] flex items-center bg-[#aeceb7] shadow-[0px_3.53px_8.83px_#00000040] rounded-t-[15px] md:rounded-t-[20px]">
                    <div className="w-auto h-[24px] md:h-[28px] lg:h-[30px] ml-4 md:ml-8 lg:ml-[39.8px] text-white text-base md:text-xl lg:text-[24.7px] tracking-[1.60px] md:tracking-[2.20px] lg:tracking-[2.47px] flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal] whitespace-nowrap">
                        AI 評論回覆建議
                    </div>
                </div>

                {/* 用戶信息區域 - 响应式 */}
                <div className="ml-0 w-full min-h-[65px] md:h-[81.28px] relative px-3 md:px-0 py-2 md:py-0">
                    <div className="absolute top-[calc(50.00%_-_26px)] md:top-[calc(50.00%_-_33px)] left-3 md:left-[34px] w-[50px] h-[50px] md:w-[66px] md:h-[66px] flex items-center justify-center">
                        <div className="mt-[0.7px] h-[34px] md:h-[44.17px] ml-[0.7px] w-[34px] md:w-[44.17px] bg-[#d9d9d9] rounded-[17px] md:rounded-[22.09px]" />
                    </div>

                    <div className="absolute top-[calc(50.00%_-_26px)] md:top-[calc(50.00%_-_33px)] left-[60px] md:left-[110px] w-52 h-[24px] md:h-[30px] flex items-center">
                        <div className="flex items-center justify-center mt-[1px] h-[14px] md:h-[17px] ml-1 md:ml-[9.7px] w-auto [font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#1a1a1a] text-xs md:text-[14.1px] tracking-[0.80px] md:tracking-[1.41px] leading-[normal] truncate max-w-[200px] md:max-w-none">
                            {comment.username}
                        </div>
                    </div>

                    {/* 星級評分和日期 - 响应式 */}
                    <div className="absolute top-[50.00%] left-[60px] md:left-[110px] w-auto h-[24px] md:h-[30px] flex items-center gap-1.5 md:gap-2 flex-wrap">
                        {/* 星級 - 响应式 */}
                        <div className="flex items-center gap-0.5 md:gap-[6px]">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <img
                                    key={star}
                                    className="w-[11px] h-[10px] md:w-[15px] md:h-3.5"
                                    alt="Star"
                                    src={star <= rating ? "/img/star-3-8.svg" : "/img/star-4-8.svg"}
                                />
                            ))}
                        </div>

                        {/* 日期時間 - 响应式 */}
                        <div className="text-[#999898] text-[8px] md:text-[10.6px] tracking-[0] flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal] whitespace-nowrap">
                            {formatDate(comment.relative_date)}
                        </div>
                    </div>
                </div>

                {/* 評論內容 - 响应式 */}
                <div className="ml-0 w-full px-3 md:px-6 lg:px-9 py-2 md:py-4 flex items-start">
                    <div className="w-full text-black text-xs md:text-base tracking-[1.00px] md:tracking-[1.60px] text-left [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-relaxed md:leading-[normal] break-words">
                        {comment.caption}
                    </div>
                </div>

                {/* AI 生成回覆區域 - 响应式 */}
                <div className="ml-0 w-full flex-1 flex items-center justify-center px-3 md:px-8 lg:px-12 pb-4 md:pb-10 lg:pb-12 min-h-[300px] md:min-h-[400px]">
                    <div className="h-[300px] md:h-[400px] lg:h-[524.76px] w-full relative rounded-[15px] md:rounded-[17.67px] overflow-hidden border-[0.88px] border-solid border-[#939393] bg-white">
                        <div className="absolute top-3 md:top-[13px] left-3 md:left-[21px] h-[18px] md:h-[21px] text-black text-sm md:text-base lg:text-[17.7px] tracking-[1.20px] md:tracking-[1.77px] whitespace-nowrap flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                            AI生成回覆內容
                        </div>

                        {/* 如果還沒生成回覆，顯示生成按鈕 - 响应式 */}
                        {generatedReplies.length === 0 ? (
                            <button
                                onClick={handleGenerateReply}
                                disabled={isGenerating}
                                className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[160px] md:w-[180px] lg:w-[200px] h-[45px] md:h-[48px] lg:h-[50px] flex items-center justify-center bg-[#7da387] rounded-[8px] md:rounded-[10px] overflow-hidden hover:bg-[#6d9377] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="h-5 md:h-6 w-auto px-3 md:px-4 text-white text-base md:text-lg lg:text-xl tracking-[2.40px] md:tracking-[3.20px] lg:tracking-[4.00px] whitespace-nowrap flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                    {isGenerating ? '生成中...' : '點擊生成回覆'}
                                </div>
                            </button>
                        ) : (
                            /* 顯示生成的回覆列表 - 响应式 */
                            <div className="absolute top-[45px] md:top-[50px] left-0 right-0 bottom-0 flex flex-col overflow-hidden">
                                {/* 回复列表滚动区域 */}
                                <div className="flex-1 overflow-y-auto px-3 md:px-5 py-2">
                                    <div className="flex flex-col gap-2 md:gap-3 pb-2">
                                        {generatedReplies.map((reply) => (
                                            <div
                                                key={reply.id}
                                                onClick={() => handleSelectReply(reply.id)}
                                                className={`relative flex items-start gap-2 md:gap-3 p-3 md:p-4 rounded-lg cursor-pointer transition-colors ${selectedReplyId === reply.id
                                                    ? 'bg-[#e8e8dc]'
                                                    : 'bg-white hover:bg-gray-50'
                                                    }`}
                                            >
                                                {/* Checkbox - 响应式 */}
                                                <div className="flex-shrink-0 mt-0.5 md:mt-1">
                                                    <div className={`w-5 h-5 md:w-6 md:h-6 rounded border-2 flex items-center justify-center transition-colors ${selectedReplyId === reply.id
                                                        ? 'bg-[#7da387] border-[#7da387]'
                                                        : 'bg-[#d9d9d9] border-[#d9d9d9]'
                                                        }`}>
                                                        {selectedReplyId === reply.id && (
                                                            <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path d="M5 13l4 4L19 7"></path>
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* 回覆內容 - 响应式 */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-black text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] leading-[1.6] [font-family:'Noto_Sans_TC',Helvetica] font-bold mb-1">
                                                        生成回覆 {reply.id}
                                                    </div>
                                                    <div className="text-black text-xs md:text-sm tracking-[1.00px] md:tracking-[1.40px] leading-[1.7] md:leading-[1.8] [font-family:'Noto_Sans_TC',Helvetica] font-normal">
                                                        {reply.text}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 複製回覆按鈕 - 固定在底部，不遮挡内容 */}
                                <div className="flex-shrink-0 py-3 px-3 md:px-5 bg-white border-t border-gray-200">
                                    <button
                                        onClick={handleCopyReply}
                                        disabled={selectedReplyId === null}
                                        className="w-full md:w-[140px] mx-auto h-[40px] md:h-[45px] flex items-center justify-center bg-[#7da387] rounded-[8px] md:rounded-[10px] overflow-hidden hover:bg-[#6d9377] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                    >
                                        <div className="text-white text-sm md:text-base tracking-[1.20px] md:tracking-[1.60px] whitespace-nowrap flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                            複製回覆
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

